<template>
  <Teleport to="body">
    <TransitionGroup
      :name="transitionName"
      tag="div"
      class="app-notifications"
      :class="`app-notifications--${position}`"
    >
      <div
        v-for="item in items"
        :key="item.id"
        class="app-notifications__item"
        :class="[
          `app-notifications__item--${item.type}`,
          { 'app-notifications__item--reward': item.kind === 'reward' },
        ]"
        @mouseenter="pauseNotification(item.id)"
        @mouseleave="resumeNotification(item.id)"
        @pointerdown.stop
        @mousedown.stop
        @click.stop="handleItemClick(item)"
      >
        <template v-if="item.kind === 'reward' && item.reward">
          <div class="app-notifications__icon app-notifications__icon--reward">
            <TokenIcon />
          </div>
          <div class="app-notifications__message app-notifications__message--reward">
            <div class="app-notifications__reward-title">{{ t("onboarding.rewardToastTitle") }}</div>
            <div class="app-notifications__reward-quest">{{ item.reward.questName }}</div>
            <div class="app-notifications__reward-amount">
              {{ t("onboarding.rewardAmount", { amount: item.reward.amount }) }}
            </div>
          </div>
        </template>
        <template v-else>
          <div
            v-if="showIcons"
            class="app-notifications__icon"
          >
            <svg
              v-if="item.type === 'success'"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
              />
              <polyline points="9,12 11.5,14.5 15.5,9.5" />
            </svg>
            <svg
              v-else-if="item.type === 'error'"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
              />
              <line
                x1="12"
                y1="8"
                x2="12"
                y2="13"
              />
              <circle
                cx="12"
                cy="16"
                r="0.5"
                fill="currentColor"
              />
            </svg>
            <svg
              v-else
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
              />
              <circle
                cx="12"
                cy="8.5"
                r="0.5"
                fill="currentColor"
              />
              <line
                x1="12"
                y1="11"
                x2="12"
                y2="16"
              />
            </svg>
          </div>

          <div class="app-notifications__content">
            <div
              class="app-notifications__message"
              v-html="item.message"
            ></div>
            <button
              v-if="item.action"
              type="button"
              class="app-notifications__action"
              @click.stop="handleAction(item)"
            >
              {{ item.action.label }}
            </button>
          </div>
        </template>

        <button
          class="app-notifications__close"
          @click.stop="removeNotification(item.id)"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line
              x1="18"
              y1="6"
              x2="6"
              y2="18"
            />
            <line
              x1="6"
              y1="6"
              x2="18"
              y2="18"
            />
          </svg>
        </button>

        <div
          v-if="!item.sticky"
          class="app-notifications__progress"
          :class="{ 'app-notifications__progress--paused': item.paused }"
        >
          <div
            class="app-notifications__progress-bar"
            :style="{ animationDuration: `${item.displayTime}ms` }"
          ></div>
        </div>
      </div>
    </TransitionGroup>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useNotifications, type NotificationItem, type NotificationKind } from "../store/useNotifications";
import TokenIcon from "../assets/icons/TokenIcon.vue";

type NotificationPosition = "top-right" | "bottom-center";

interface Props {
  showIcons?: boolean;
  position?: NotificationPosition;
  /** Показывать только уведомления этого вида; без значения — все виды в одном стеке. */
  kind?: NotificationKind;
}

const props = withDefaults(defineProps<Props>(), {
  showIcons: false,
  position: "bottom-center",
  kind: undefined,
});

const { t } = useI18n();

const transitionName = computed(() => (props.position === "bottom-center" ? "toast-bottom" : "toast"));

const store = useNotifications();
const { notifications } = storeToRefs(store);
const { pauseNotification, resumeNotification, removeNotification } = store;

const items = computed(() =>
  props.kind ? notifications.value.filter((item) => (item.kind ?? "default") === props.kind) : notifications.value,
);

function handleItemClick(item: NotificationItem) {
  if (item.kind === "reward") {
    item.onClick?.();
  }
}

/** Действие тоста выполнено — сам тост больше не нужен (CAB-782). */
function handleAction(item: NotificationItem) {
  item.action?.handler();
  removeNotification(item.id);
}
</script>
